<template>
    <div class="reservation-form">
        <div class="containter" v-if="showFormTitle">
            <div class="row mb-3">
                <div class="col">
                    <h4>Nova reserva</h4>
                </div>
            </div>
        </div>
        <form v-on:submit.prevent="handleSubmit">
            <div class="container-fluid">
                <div class="row">
                    <div class="col-md-5 form-group">
                        <label for="name">Nome</label>
                        <input class="form-control" v-model="formValues.name" name="name" id="name" required>
                        <span class="form-error">{{ validationMessage('name') }}</span>
                    </div>
                    <div class="col-md-7 form-group">
                        <label for="description">Descrição</label>
                        <input class="form-control" v-model="formValues.description" name="description" id="description">
                        <span class="form-error">{{ validationMessage('description') }}</span>
                    </div>
                </div>
                <div class="row">
                    <div class="col-md-6 form-group">
                        <label for="schedule_time_minutes">Produto</label>
                        <select class="form-control" v-model="formValues.id_product" required name="id_product" id="id_product" @change="handleProductChange">
                            <option
                                v-for="product in filteredProducts"
                                :value="product.id"
                                :key="product.id">
                                {{ product.name }}
                            </option>;
                        </select>
                        <span class="form-error">{{ validationMessage('id_product') }}</span>
                    </div>
                    <div class="col-md-6 form-group">
                        <label for="schedule_date">Data</label>
                        <select class="form-control" :disabled="productLoading" v-model="formValues.schedule_date" name="schedule_date" id="schedule_date" @change="handleScheduleChange">
                            <option v-for="option in dateOptions" :value="option.value" :key="option.value">
                                {{ option.label }}
                            </option>
                        </select>
                        <span class="form-error">{{ validationMessage('schedule_date') }}</span>
                    </div>
                </div>
                <div class="row">
                    <div class="col-md-6 form-group">
                        <label for="schedule_time_minutes">Horário</label>
                        <select class="form-control" :disabled="!isScheduleTimeMinutesAvailable" v-model="formValues.schedule_time_minutes" name="schedule_time_minutes" id="schedule_time_minutes" @change="handleScheduleChange">
                            <option v-for="option in hourOptions" :value="option.value" :key="option.value">
                                {{ option.label }}
                            </option>
                        </select>
                        <span class="form-error">{{ validationMessage('schedule_time_minutes') }}</span>
                    </div>
                    <div class="col-md-6 form-group">
                        <label for="duration_minutes">Duração</label>
                        <select class="form-control" v-model="formValues.duration_minutes" required name="duration_minutes" id="duration_minutes" @change="handleScheduleChange">
                            <option v-for="option in durationMinutesOptions" :value="option.value" :key="option.value">
                                {{ option.label }}
                            </option>
                        </select>
                        <span class="form-error">{{ validationMessage('duration_minutes') }}</span>
                    </div>
                </div>
                <div v-if="groupedTariffs.length > 0">
                    <h4>Tarifas</h4>
                    <span class="unavailable" v-if="!tariffSumIsAvailable">Indisponíveis</span>
                </div>
                <div class="row">
                    <div v-for="tariff in groupedTariffs" :key="tariff.id" class="col-md-4 mb-1">
                        <label :for="`tariffs[${tariff.id}]`">{{ tariff.type.name }}</label>
                        <input class="form-control" v-model="formValues.tariffs[tariff.id]" :id="`tariffs[${tariff.id}]`" :name="`tariffs[${tariff.id}]`" type="number" required min="0">
                        <span class="unavailable" v-if="tariffIsUnavailable(tariff.id)">Indisponível</span>
                    </div>
                </div>
                <div v-if="shouldSelectResources">
                    <h4>Lugares</h4>
                    <div v-if="seatMapOptions">
                        <ReservationResourceSelector
                            :seatMap="seatMapOptions"
                            :currentResourceIds="[]"
                            @onSelectedResources="handleResourceSelectionChange"
                        />
                        <div v-for="err in resourceSelectionErrors">
                            <span class="unavailable">{{ err }}</span>
                        </div>
                        <span class="form-error d-block">{{ validationMessage('resource_ids') }}</span>
                    </div>
                    <div v-else>Carregando...</div>
                </div>
                <div class="row">
                    <div class="col-md-2 offset-md-8 form-group">
                        <button type="button" class="btn btn-outline-primary btn-block" v-on:click.prevent="onCancel">Cancelar</button>
                    </div>
                    <div class="col-md-2 form-group">
                        <button type="submit" class="btn btn-primary btn-block" :disabled="tariffIsEmpty" :title="messageTariffIsEmpty">Salvar</button>
                        <span class="form-error d-block text-center mt-1" >{{ validationMessage('tariffs') }}</span>
                    </div>
                </div>
            </div>
        </form>
    </div>
</template>

<script>
import moment from 'moment';
import ReservationResourceSelector from './ReservationResourceSelector';
import { csrfToken, requestJson, validation, confirmValidationErrors, confirmedVerifications } from '../helpers';
import CheckDateAvaliable from "./mixins/CheckDateAvaliable";

export default {
    mixins: [CheckDateAvaliable],
    components: { ReservationResourceSelector },

    props: {
        products: Array,
        reservation: Object,
        onCancel: Function,
        onSubmit: Function,
        showFormTitle: {
            type: Boolean,
            default: true
        }
    },

    data(){
        const product = this.products.length ? this.products[0] : null;

        return {
            productAvailableSchedulings: [],
            productLoading: true,
            formValues: {
                name: '',
                description: '',
                id_product: product ? product.id : '',
                schedule_date: '',
                schedule_time_minutes: '',
                duration_minutes: '',
                tariffs: {},
            },
            fromDate: moment(),
            toDate: moment().add(6, 'months'),
            seatMapOptions: null,
            selectedResources: [],
            schedulings: null,
            scheduleTimeMinutesOptions: null,
            abortController: null,
            requestCounter: 0,
            searchDate: () => {
                const schedulings = this.schedulings.filter(dates => dates.date === this.formValues.schedule_date);
                return { date: this.formValues.schedule_date, schedulings };
            },
            getProduct: () => this.selectedProduct,
            dateSelected: () => this.selectedDate,
        };
    },

    computed: {
        filteredProducts(){
            return this.products.filter(prod =>
                prod.active &&
                prod.tariff_groups.some(tg => tg.tariffs.length > 0)
            );
        },
        durationMinutesOptions(){
            return this.selectedProduct ? this.selectedProduct.scheduling_options.duration_minutes : [];
        },
        csrfToken(){
            return csrfToken();
        },
        selectedProduct(){
            for(let product of this.products){
                if(product.id == this.formValues.id_product){
                    return product;
                }
            }

            return null;
        },
        tariffIsEmpty() {
            return !(Object.values(this.formValues.tariffs).some(tariff => tariff > 0));
        },
        messageTariffIsEmpty(){
            return this.tariffIsEmpty ? 'Selecione ao menos uma tarifa' : '';
        },
        selectedDate(){
            return this.dateOptions?.find(date => date.value === this.formValues.schedule_date);
        },
        selectedTimeMinutes(){
            return this.hourOptions.find(time => time.value === this.formValues.schedule_time_minutes);
        },
        productWithDateOnly(){
            return this.selectedProduct && this.selectedProduct.selling_mode === 'with_date_only';
        },
        selectedScheduling(){
            const filtered = this.schedulings.filter(dates => dates.date === this.formValues.schedule_date);
            if (filtered.length == 0) {
                return null;
            }

            return this.productWithDateOnly ? filtered[0] : filtered.find(sch => sch.availableScheduleTimes.some(st => st.minutes == this.formValues.schedule_time_minutes));
        },
        defaultDurationMinutesValue(){
            const initialMinute = this.durationMinutesOptions.length > 0 ? this.durationMinutesOptions[0].value : '';
            return this.selectedScheduling?.durationMinutes ?? initialMinute;
        },
        groupedTariffs(){
            if(!this.productAvailableSchedulings || !this.formValues.schedule_date || (!this.productWithDateOnly && !this.formValues.schedule_time_minutes)){
                return [];
            }

            if (this.dateTimeIsAvailable) {
                return this.selectedScheduling.finalTariffGroup.tariffs;
            }

            return this.defaultTariffGroup ? this.defaultTariffGroup.tariffs : [];
        },
        dateTimeIsAvailable(){
            if(this.selectedDate && !this.selectedDate.available){
                return false;
            }

            if(!this.productWithDateOnly && this.selectedTimeMinutes && !this.selectedTimeMinutes.available) {
                return false;
            }

            return true;
        },
        tariffFormValueSum(){
            return this.groupedTariffs.reduce((sum, t) => sum + parseInt(this.formValues.tariffs[t.id] ?? 0), 0);
        },
        tariffSumIsAvailable(){
            if(!this.selectedScheduling) {
                return false;
            }

            if (this.productWithDateOnly) {
                const { ok } = validation.productScheduleDateTimeIsAvailable(this.productAvailableSchedulings, moment(this.formValues.schedule_date), null, this.tariffFormValueSum);
                return ok;
            }

            if(!this.selectedTimeMinutes.available){
                return false;
            }

            const { ok } = validation.productScheduleDateTimeIsAvailable(this.productAvailableSchedulings, moment(this.formValues.schedule_date), this.formValues.schedule_time_minutes, this.tariffFormValueSum);
            return ok;
        },
        shouldSelectResources(){
            if (this.groupedTariffs.length === 0) {
                return false;
            }

            return !!this.selectedScheduling?.requiresSeatSelection;
        },
        selectedScheduleTimeMinutesInt(){
            return parseInt(this.formValues.schedule_time_minutes, 10);
        },
        resourceSelectionErrors(){
            const arr = [];

            const { ok, message } = validation.selectedResourcesCapacityMatchTariffQuantities(this.selectedResources, this.tariffFormValueSum);
            if (!ok) {
                arr.push(message);
            }

            return arr;
        },
        defaultTariffGroup(){
            return this.selectedProduct ? this.selectedProduct.tariff_groups.find(group => group.is_default) : null;
        },
    },

    methods: {
        async requestProductAvailableDates(){
            if(!this.selectedProduct){
                return;
            }
            const from = this.fromDate.format('YYYY-MM-DD');
            const to = this.toDate.format('YYYY-MM-DD');
            
            // Cancel any previous in-flight request
            if (this.abortController) {
                this.abortController.abort();
            }
             
            this.requestCounter++;
            const currentRequest = this.requestCounter;
            
            this.abortController = new AbortController();
            this.productLoading = true;
            const params = { from, to };
            
            try {
                const data = await requestJson(
                    this.selectedProduct.available_schedulings_url,
                    params,
                    'GET',
                    false,
                    this.abortController.signal
                );

                this.productAvailableSchedulings = data;
                this.schedulings = data;
                this.scheduleTimeMinutesOptions = this.selectedProduct.scheduling_options.consumable_from_minutes;
            } catch (err) {
                console.error(err);
            } finally {
                if (currentRequest === this.requestCounter) {
                    this.productLoading = false;
                    this.abortController = null;
                }
            }
        },
        validationMessage(key){
            const validation = this.reservation && this.reservation.validation;

            if (validation && validation[key]){
                return validation[key].join(', ');
            }

            return '';
        },
        handleProductChange(event){
            this.resetForm(event);
            this.requestProductAvailableDates();
        },
        handleScheduleChange(event){
            this.resetForm(event);
            this.requestSeatMapOptions();
        },
        resetTariffs(){
            this.formValues.tariffs = this.groupedTariffs.reduce((c, t) => ({ ...c, [t.id]: 0 }), {});
        },
        resetForm(event){
            const eventName = event ? event.target.name : '';

            if(this.productWithDateOnly){
                this.resetTariffs();
            }

            switch (eventName) {
                case 'id_product':
                    this.formValues.schedule_date = '';
                    this.formValues.schedule_time_minutes = '';
                    this.formValues.duration_minutes = '';
                    break;
                case 'schedule_date':
                    this.formValues.schedule_time_minutes = '';
                    this.formValues.duration_minutes = this.productWithDateOnly ? this.defaultDurationMinutesValue : '';
                    break;
                case 'schedule_time_minutes':
                    this.formValues.duration_minutes = this.defaultDurationMinutesValue;
                    this.resetTariffs();
                    break;
            }
        },
        tariffIsUnavailable(idTariff){
            if(!this.dateTimeIsAvailable || !this.tariffSumIsAvailable){
                return this.formValues.tariffs[idTariff] != 0;
            }

            const tariff = this.groupedTariffs.find(t => t.id == idTariff);
            if(this.productWithDateOnly){
                const { ok } = validation.tariffScheduleDateTimeIsAvailable(this.productAvailableSchedulings, tariff, moment(this.selectedScheduling.date), null, this.formValues.tariffs[idTariff]);
                return !ok;
            }

            const { ok } = validation.tariffScheduleDateTimeIsAvailable(this.productAvailableSchedulings, tariff, moment(this.selectedScheduling.date), this.formValues.schedule_time_minutes, this.formValues.tariffs[idTariff]);
            return !ok;
        },
        async requestSeatMapOptions(){
            this.seatMapOptions = null;

            if (!this.shouldSelectResources) {
                return;
            }

            const { schedule_date, schedule_time_minutes, duration_minutes } = this.formValues;

            this.seatMapOptions = await requestJson(this.selectedScheduling.id_seat_map_tariff_group_url, { schedule_date, schedule_time_minutes, duration_minutes });
        },
        handleResourceSelectionChange(resources){
            this.selectedResources = resources;
        },
        handleSubmit(){
            if(this.tariffIsEmpty) {
                return;
            }

            const errors = this.confirmationErrors();
            if (errors.length && !confirmValidationErrors(errors)) {
                return;
            }

            const data = {
                _token: this.csrfToken,
                ...this.formValues,
                resource_ids: this.selectedResources.map(res => res.id).join(','),
                verifications: confirmedVerifications(errors)
            };

            this.onSubmit(data);
        },
        confirmationErrors(){
            const momentDate = moment(this.formValues.schedule_date);
            const minutes = this.formValues.schedule_time_minutes;

            const arr = [];

            arr.push(validation.productScheduleContainsDate(this.productAvailableSchedulings, momentDate));

            if (!this.productWithDateOnly) {
                arr.push(validation.productScheduleContainsDateTime(this.productAvailableSchedulings, momentDate, minutes));
            }

            arr.push(validation.productScheduleDateTimeIsAvailable(this.productAvailableSchedulings, momentDate, this.productWithDateOnly ? null : minutes, this.tariffFormValueSum));

            for (let tariff of this.groupedTariffs) {
                arr.push(validation.tariffScheduleDateTimeIsAvailable(this.productAvailableSchedulings, tariff, momentDate, this.productWithDateOnly ? null : minutes, this.formValues.tariffs[tariff.id]));
            }

            if (this.shouldSelectResources) {
                for (let res of this.selectedResources) {
                    arr.push({
                        ...validation.resourceIsAvailable(this.seatMapOptions, res, []),
                        verifications: ['ignore_resource_availability']
                    });
                }

                arr.push({
                    ...validation.selectedResourcesCapacityMatchTariffQuantities(this.selectedResources, this.tariffFormValueSum),
                    verifications: ['ignore_resources_total_capacity']
                });
            }

            return arr;
        },
    },

    mounted(){
        this.requestProductAvailableDates();
    },
    beforeDestroy(){
        // Cancel any pending requests when component is destroyed
        if (this.abortController) {
            this.abortController.abort();
        }
    }
}
</script>

<style scoped>
.reservation-form form {
    border: 1px solid #ccc;
    padding: 1rem 0;
    border-radius: 5px;
    background: #f5f5f5;
}
.form-error {
    color: #ea4335;
}

.unavailable{
    font-size: small;
    font-weight: bold;
    color: #ea4335;
}
</style>
